import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Router, NavigationEnd } from "@angular/router";
import { filter, first, map, catchError } from "rxjs/operators";
import { of } from "rxjs";

@Injectable({ providedIn: "root" })
export class TagManagerService {
  private initialized = false;
  private gtmId: string | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  // Initialize via retailer's provided GTM ID (preferred)
  initWithId(id: string | null | undefined): void {
    if (this.initialized || !id) return;
    if (!this.validateGtmId(id)) return;
    this.gtmId = id;
    this.injectGtm(id);
    this.initialized = true;
    this.setupSpaTracking();
  }

  // Initialize: fetch GTM ID from subdomain API and inject GTM (fallback)
  //   init(): void {
  //     if (
  //       this.initialized ||
  //       typeof window === "undefined" ||
  //       typeof document === "undefined"
  //     ) {
  //       return;
  //     }

  //     const subdomain = this.getSubdomain(window.location.hostname);
  //     if (!subdomain) return;

  //     const url =
  //       "https://portal.payar.co.uk/api/v38/get-retailer-from-subdomain";
  //     const params = new HttpParams().set("subdomain", subdomain);

  //     this.http
  //       .get<any>(url, { params })
  //       .pipe(
  //         first(),
  //         map((resp) => this.extractGtmId(resp)),
  //         catchError(() => of(null))
  //       )
  //       .subscribe((gtm) => {
  //         if (gtm && this.validateGtmId(gtm)) {
  //           this.gtmId = gtm;
  //           this.injectGtm(gtm);
  //           this.initialized = true;
  //           this.setupSpaTracking();
  //         }
  //       });
  //   }

  // Extract subdomain (e.g., demo.toodleweb.app -> demo)
  private getSubdomain(hostname: string): string | null {
    if (!hostname) return null;
    const parts = hostname.split(".");
    if (parts.length < 2) return null;
    return parts[0] || null;
  }

  // Try common response shapes for GTM id
  private extractGtmId(resp: any): string | null {
    if (!resp) return null;
    const candidates: Array<string | null | undefined> = [
      resp.gtm_id,
      resp.gtmId,
      resp.google_tag_manager_id,
      resp.googleTagManagerId,
      resp?.data?.gtm_id,
      resp?.data?.gtmId,
      resp?.data?.google_tag_manager_id,
    ];
    const value = candidates.find((v) => typeof v === "string") as
      | string
      | undefined;
    return value ?? null;
  }

  private validateGtmId(id: string): boolean {
    return /^GTM-[A-Z0-9]{4,12}$/i.test(id);
  }

  // private injectGtm(gtmId: string): void {
  //   console.log("Injecting GTM with ID:", gtmId);
  //   (window as any).dataLayer = (window as any).dataLayer || [];
  //   console.log("dataLayer after init:", (window as any).dataLayer); // Add this line
  //   (window as any).dataLayer.push({ event: "gtm_init" });
  //   console.log("After gtm_init push:", (window as any).dataLayer);

  //   const script = document.createElement("script");
  //   script.async = true;
  //   script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
  //     gtmId
  //   )}`;
  //   document.head.appendChild(script);

  //   const noscript = document.createElement("noscript");
  //   const iframe = document.createElement("iframe");
  //   iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
  //     gtmId
  //   )}`;
  //   iframe.height = "0";
  //   iframe.width = "0";
  //   (iframe.style as any).display = "none";
  //   (iframe.style as any).visibility = "hidden";
  //   noscript.appendChild(iframe);
  //   document.body.insertBefore(noscript, document.body.firstChild);
  // }

  private injectGtm(gtmId: string): void {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    (window as any).dataLayer = (window as any).dataLayer || [];

    (window as any).dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);

    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";

    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  // Virtual page view tracking for SPA
  private setupSpaTracking(): void {
    if (!this.gtmId) return;

    this.pushVirtualPageView(window.location.pathname + window.location.search);

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.pushVirtualPageView(
          e.urlAfterRedirects || e.url || window.location.pathname
        );
      });
  }

  private pushVirtualPageView(url: string): void {
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "virtual_page_view",
        page: {
          url,
          title: document.title,
        },
      });
      (window as any).dataLayer.push({
        event: "history_change",
        page_path: url,
      });
    } catch {
      // no-op
    }
  }
}
